import React from 'react';
import { customerStatus, displayName, formatDate, formatStorageSize, sumUsage, toMillis } from './customerFormat';

// Overview tab (round 4): honest counts derived straight from the customers
// already in redux (no extra reads) plus the Firebase/security snapshot the
// parent (AdminDashboard.jsx) fetches once via getOpsOverview. Replaces the
// old fake "Active Projects" / "Total Revenue" StatCards with real numbers
// from usageStats (see customerFormat.sumUsage).
const OverviewTab = ({ customers, opsOverviewStatus, securitySweep }) => {
  const usage = sumUsage(customers);
  const hasUsage = usage.counted > 0;

  const activeCount = customers.filter((c) => customerStatus(c).key === 'active').length;
  const invitedCount = customers.filter((c) => customerStatus(c).key === 'invited').length;
  const statusParts = [];
  if (activeCount > 0) statusParts.push(`${activeCount} active`);
  if (invitedCount > 0) statusParts.push(`${invitedCount} invited`);
  const customersSub = statusParts.length > 0 ? statusParts.join(' · ') : null;

  const usageFooter = usage.latestComputedAt
    ? `Usage counted nightly · last ${formatDate(usage.latestComputedAt)}`
    : 'Usage counts appear after the first nightly run (3:30)';
  const countedNote =
    usage.counted < usage.total && usage.counted > 0
      ? ` · ${usage.counted} of ${usage.total} customers counted`
      : '';

  const latestCustomers = [...customers]
    .sort((a, b) => (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatCard title="Customers" value={usage.total} sub={customersSub} />
          <StatCard
            title="Workers"
            value={hasUsage ? usage.employees : '—'}
            sub={hasUsage ? null : 'counted nightly at 3:30'}
          />
          <StatCard
            title="Jobsites"
            value={hasUsage ? usage.jobsites : '—'}
            sub={hasUsage ? null : 'counted nightly at 3:30'}
          />
          <StatCard
            title="Storage"
            value={hasUsage ? formatStorageSize(usage.storageBytes) : '—'}
            sub={hasUsage ? `${usage.storageObjects} files` : 'counted nightly at 3:30'}
          />
        </div>
        <p className="mt-3 text-xs text-[#64798a]">
          {usageFooter}
          {countedNote}
        </p>
      </div>

      <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
        <h2 className="mb-4 text-xl font-bold text-[#1d2b33]">Latest customers</h2>
        {latestCustomers.length === 0 ? (
          <p className="text-[#5b6d7a]">No customers yet.</p>
        ) : (
          <div>
            {latestCustomers.map((customer) => {
              const status = customerStatus(customer);
              return (
                <div
                  key={customer.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgba(150,165,180,.15)] py-2 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1d2b33]">{displayName(customer)}</p>
                    <p className="truncate text-xs text-[#64798a]">{customer.ownerName || '—'}</p>
                  </div>
                  <div className="flex flex-none items-center gap-2" title={status.hint}>
                    <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: status.color }} />
                    <span className="text-xs font-semibold text-[#1d2b33]">{status.label}</span>
                  </div>
                  <p className="flex-none text-xs text-[#64798a]">{formatDate(customer.createdAt)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
          Firebase &amp; security
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SecurityCard status={opsOverviewStatus} securitySweep={securitySweep} />
          <DataProtectionCard />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, sub }) => (
  <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-4 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
    <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">{title}</h3>
    <p className="mt-1 text-2xl font-bold text-[#1d2b33]">{value}</p>
    {sub && <p className="mt-1 text-xs text-[#64798a]">{sub}</p>}
  </div>
);

// result values written by scripts/securityCheck.js to metadata/securitySweep
// (see adminDashboardF.js's getOpsOverview): SECURE (full sweep, clean),
// SECURE_PARTIAL (--groups=... partial sweep, clean), UNVERIFIED (infra
// throttle mid-sweep, not a leak), FAILURES (real gate failure). Colors
// reuse this same portal's existing green/amber/red trio from
// CompanyFrontPage.jsx (#2ea183 / #e0a23c / #d96a5b) for a consistent
// palette across the app.
const SECURITY_RESULT_STYLES = {
  SECURE: { dot: '#2ea183', label: 'Secure' },
  SECURE_PARTIAL: { dot: '#e0a23c', label: 'Secure (partial sweep)' },
  UNVERIFIED: { dot: '#e0a23c', label: 'Unverified' },
  FAILURES: { dot: '#d96a5b', label: 'Security failures' },
};

// Dashboard tab "Security" card: latest metadata/securitySweep summary via
// the getOpsOverview thunk (see the useEffect above that fires it once on
// Dashboard mount). Treated as gray/"no sweep yet" for both the loading
// state and a genuinely empty securitySweep (never run) - the admin can't
// tell those apart from this card alone, which is fine, it self-resolves
// within a second either way.
const SecurityCard = ({ status, securitySweep }) => {
  const style = securitySweep ? SECURITY_RESULT_STYLES[securitySweep.result] : null;
  // A green verdict from weeks ago is not a current verdict: past 7 days the
  // dot degrades to amber and the label says so (2026-08-31 review) - the
  // full sweep is the customer-handoff gate, so this card must not show a
  // reassuring stale green.
  const sweepAgeMs = securitySweep?.at ? Date.now() - new Date(securitySweep.at).getTime() : 0;
  const isStale = Number.isFinite(sweepAgeMs) && sweepAgeMs > 7 * 24 * 60 * 60 * 1000;
  const dotColor = isStale ? '#e0a23c' : style?.dot || '#96a5b4';
  const label = `${style?.label || securitySweep?.result || ''}${isStale ? ' (stale)' : ''}`;

  return (
    <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-4 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Security</h3>

      {status === 'failed' && (
        <p className="mt-2 text-sm text-[#d96a5b]">Could not load the latest sweep result.</p>
      )}

      {status !== 'failed' && !securitySweep && (
        <div className="mt-2 flex items-center gap-2">
          <span className="h-2.5 w-2.5 flex-none rounded-full bg-[#96a5b4]" />
          <p className="text-sm text-[#64798a]">
            {status === 'succeeded' ? 'No sweep recorded yet.' : 'Loading...'}
          </p>
        </div>
      )}

      {securitySweep && (
        <>
          <div className="mt-2 flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 flex-none rounded-full"
              style={{ backgroundColor: dotColor }}
            />
            <p className="text-sm font-semibold text-[#1d2b33]">
              {label}
            </p>
          </div>
          <p className="mt-1 text-xs text-[#64798a]">
            {securitySweep.tested} tested · {securitySweep.failures} failures
            {securitySweep.unverified ? ` · ${securitySweep.unverified} unverified` : ''}
            {' · '}
            {securitySweep.mode === 'partial' ? `partial (${securitySweep.groups})` : 'full sweep'}
          </p>
          <p className="mt-1 text-xs text-[#64798a]">
            {securitySweep.at ? new Date(securitySweep.at).toLocaleString() : ''}
          </p>
        </>
      )}
    </div>
  );
};

// Static "Data protection" card - infra facts, not derived from any
// callable (Firestore PITR/backup schedule and Storage bucket retention
// live in Google Cloud console settings, not in a document this portal can
// read). Update the copy by hand if the owner changes the retention setup;
// re-date "verified" when re-confirmed against the console.
const DataProtectionCard = () => (
  <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-4 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
    <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Data protection</h3>
    <ul className="mt-2 space-y-1 text-sm text-[#1d2b33]">
      <li>7-day rewind (PITR)</li>
      <li>Daily backups kept 7 days</li>
      <li>Weekly backups kept 14 weeks</li>
      <li>Deleted files recoverable 30 days</li>
    </ul>
    <p className="mt-2 text-xs text-[#64798a]">Verified 2026-08-31</p>
  </div>
);

export default OverviewTab;
